// react and js
import PropTypes from "prop-types";
import { React, useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";

// mui
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Typography,
  Box,
  Stack,
  Collapse,
  FormControl,
  Select,
  NativeSelect,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// components
import backendRequest from "../../components/BackendRequest";
import CardOutlet from "../../components/CardOutlet";
import LoadingAnimation from "../../components/LoadingAnimation";
import MissingContent from "../../components/MissingContent";

// hooks
import appContext from "../../hooks/appContext";
import { stateStorageInit } from "../../hooks/stateStorageInit";

// layouts

// pages

// theme
import { B3Tiny, CardOutlinedInput, CardMenuItem } from "../../theme/styled";

// utils
import { TranslatedMessage } from "../../utils/common";

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
function FetchTable({ tableArray, referenceTradingCurrency }) {
  FetchTable.propTypes = {
    tableArray: PropTypes.any.isRequired,
    referenceTradingCurrency: PropTypes.string.isRequired,
  };

  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // Currency symbol mapping
  const currencySymbols = {
    'EUR': '€',
    'USD': '$',
    'GBP': '£',
    'JPY': '¥',
    'CHF': 'CHF',
  };
  const currencySymbol = currencySymbols[referenceTradingCurrency] || referenceTradingCurrency;

  // Separate summary from individual products while maintaining order
  const products = [];
  let summary = null;

  tableArray.forEach((row) => {
    if (row.product_tag === "ALL") {
      summary = row;
    } else {
      products.push(row);
    }
  });

  return isDesktop ? (
    <TableContainer component={Box} sx={{ background: "white", borderRadius: "10px 10px 13px 13px" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}>
              <Typography variant="contentSmallBold">{t("distributorPortal.revenue.headers.product")}</Typography>
            </TableCell>
            <TableCell
              align="center"
              sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
            >
              <Typography variant="contentSmallBold">{t("distributorPortal.revenue.headers.netSales")}</Typography>
            </TableCell>
            <TableCell
              align="center"
              sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
            >
              <Typography variant="contentSmallBold">{t("distributorPortal.revenue.headers.commission")}</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Individual Products */}
          {products.map((row, index) => (
            <TableRow key={index}>
              <TableCell
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                align="left"
              >
                <Typography variant="contentSmall">
                  {row.product_name}
                </Typography>
              </TableCell>

              <TableCell
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                align="center"
              >
                <Typography variant="contentSmall">
                  {parseFloat(row.net_sales).toFixed(2)} {currencySymbol}
                </Typography>
              </TableCell>

              <TableCell
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                align="center"
              >
                <Typography variant="contentSmall">
                  {parseFloat(row.revenue_commission).toFixed(2)} {currencySymbol}
                </Typography>
              </TableCell>
            </TableRow>
          ))}

          {/* Summary Row */}
          {summary && (
            <TableRow>
              <TableCell
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                align="left"
              >
                <Typography variant="contentSmallBold">
                  {summary.product_name}
                </Typography>
              </TableCell>

              <TableCell
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                align="center"
              >
                <Typography variant="contentSmallBold">
                  {parseFloat(summary.net_sales).toFixed(2)} {currencySymbol}
                </Typography>
              </TableCell>

              <TableCell
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                align="center"
              >
                <Typography variant="contentSmallBold">
                  {parseFloat(summary.revenue_commission).toFixed(2)} {currencySymbol}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  ) : (
    <Box>
      {/* Individual Products */}
      {products.map((row, index) => (
        <Stack
          key={index}
          spacing="3px"
          sx={{ background: "white", mb: 2, px: "10px", py: "10px" }}
        >
          <Box sx={{ display: "flex" }}>
            <Box sx={{ width: "50%", pr: 1 }}>
              <Typography variant="contentSmallBold"> {t("distributorPortal.revenue.headers.product")} </Typography>
            </Box>
            <Box sx={{ width: "50%", pl: 1 }}>
              <Typography variant="contentSmall">
                {row.product_name}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex" }}>
            <Box sx={{ width: "50%", pr: 1 }}>
              <Typography variant="contentSmallBold"> {t("distributorPortal.revenue.headers.netSales")} </Typography>
            </Box>
            <Box sx={{ width: "50%", pl: 1 }}>
              <Typography variant="contentSmall">
                {parseFloat(row.net_sales).toFixed(2)} {currencySymbol}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex" }}>
            <Box sx={{ width: "50%", pr: 1 }}>
              <Typography variant="contentSmallBold"> {t("distributorPortal.revenue.headers.commission")} </Typography>
            </Box>
            <Box sx={{ width: "50%", pl: 1 }}>
              <Typography variant="contentSmall">
                {parseFloat(row.revenue_commission).toFixed(2)} {currencySymbol}
              </Typography>
            </Box>
          </Box>
        </Stack>
      ))}

      {/* Summary Row */}
      {summary && (
        <Stack
          spacing="3px"
          sx={{ background: "white", mb: 2, px: "10px", py: "10px" }}
        >
          <Box sx={{ display: "flex" }}>
            <Box sx={{ width: "50%", pr: 1 }}>
              <Typography variant="contentSmallBold"> {t("distributorPortal.revenue.headers.product")} </Typography>
            </Box>
            <Box sx={{ width: "50%", pl: 1 }}>
              <Typography variant="contentSmallBold">
                {summary.product_name}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex" }}>
            <Box sx={{ width: "50%", pr: 1 }}>
              <Typography variant="contentSmallBold"> {t("distributorPortal.revenue.headers.netSales")} </Typography>
            </Box>
            <Box sx={{ width: "50%", pl: 1 }}>
              <Typography variant="contentSmallBold">
                {parseFloat(summary.net_sales).toFixed(2)} {currencySymbol}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex" }}>
            <Box sx={{ width: "50%", pr: 1 }}>
              <Typography variant="contentSmallBold"> {t("distributorPortal.revenue.headers.commission")} </Typography>
            </Box>
            <Box sx={{ width: "50%", pl: 1 }}>
              <Typography variant="contentSmallBold">
                {parseFloat(summary.revenue_commission).toFixed(2)} {currencySymbol}
              </Typography>
            </Box>
          </Box>
        </Stack>
      )}
    </Box>
  );
}

function WidgetMainContentProcessed({ content, distributor }) {
  WidgetMainContentProcessed.propTypes = {
    content: PropTypes.any.isRequired,
    distributor: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [mobileCollapse, setMobileCollapse] = useState(false);
  useEffect(() => {
    setMobileCollapse(isDesktop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  // Group revenue commission data by period
  const groupedByPeriod = {};
  content.revenue_commission.forEach((row) => {
    if (!groupedByPeriod[row.period_display]) {
      groupedByPeriod[row.period_display] = [];
    }
    groupedByPeriod[row.period_display].push(row);
  });

  // Get list of periods (sorted by date descending)
  const periods = Object.keys(groupedByPeriod);

  // Initialize selected period from localStorage or use the first period
  const myLocalSettingStorage = JSON.parse(localStorage.getItem("myLocalSettingStorage"));
  let selectedPeriodInitValue = periods.length > 0 ? periods[0] : "";

  if (myLocalSettingStorage !== null) {
    if (
      myLocalSettingStorage.obfsc8dba69ffc954db5 !== null &&
      myLocalSettingStorage.obfsc8dba69ffc954db5 !== undefined &&
      periods.includes(myLocalSettingStorage.obfsc8dba69ffc954db5)
    ) {
      selectedPeriodInitValue = myLocalSettingStorage.obfsc8dba69ffc954db5;
    }
  }

  const [selectedPeriod, setSelectedPeriod] = useState(selectedPeriodInitValue);

  // Update selected period when periods change
  useEffect(() => {
    if (periods.length > 0 && !periods.includes(selectedPeriod)) {
      setSelectedPeriod(periods[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  return (
    <CardOutlet
      variant="outlet"
      backgroundProp="#F2F2F2"
      title={t("distributorPortal.revenue.title")}
      subheader={t("distributorPortal.revenue.subheader")}
      mainOutlet={
        <Box sx={{ mb: !mobileCollapse ? -2 : 0 }}>
          {isDesktop === false ? (
            <B3Tiny
              fullWidth
              variant="outlined"
              sx={{ mb: 2 }}
              onClick={() => {
                setMobileCollapse(!mobileCollapse);
              }}
            >
              {mobileCollapse ? t("distributorPortal.common.close") : t("distributorPortal.common.open")}
            </B3Tiny>
          ) : null}

          <Collapse
            sx={{ mx: -1, px: 1 }}
            in={mobileCollapse}
            timeout="auto"
            unmountOnExit
          >
            <Grid container spacing={2}>
              {periods.length > 0 ? (
                <>
                  <Grid item xs={12} md={12} lg={12}>
                    {isDesktop ? (
                      <Box>
                        <Typography variant="contentSmallBold">{t("distributorPortal.revenue.fields.period")}</Typography>
                        <FormControl fullWidth>
                          <Select
                            value={selectedPeriod}
                            onChange={(event) => {
                              setSelectedPeriod(event.target.value);

                              const newLocalSettingStorage = JSON.parse(localStorage.getItem("myLocalSettingStorage"));
                              newLocalSettingStorage.obfsc8dba69ffc954db5 = event.target.value;
                              localStorage.setItem("myLocalSettingStorage", JSON.stringify(newLocalSettingStorage));
                            }}
                            input={<CardOutlinedInput />}
                          >
                            {periods.map((period, index) => (
                              <CardMenuItem key={index} value={period}>
                                {period}
                              </CardMenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="contentSmallBold">{t("distributorPortal.revenue.fields.period")}</Typography>
                        <FormControl fullWidth>
                          <NativeSelect
                            value={selectedPeriod}
                            onChange={(event) => {
                              setSelectedPeriod(event.target.value);

                              const newLocalSettingStorage = JSON.parse(localStorage.getItem("myLocalSettingStorage"));
                              newLocalSettingStorage.obfsc8dba69ffc954db5 = event.target.value;
                              localStorage.setItem("myLocalSettingStorage", JSON.stringify(newLocalSettingStorage));
                            }}
                            input={<CardOutlinedInput />}
                          >
                            {periods.map((period, index) => (
                              <option key={index} value={period}>
                                {period}
                              </option>
                            ))}
                          </NativeSelect>
                        </FormControl>
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12} md={12} lg={12}>
                    {groupedByPeriod[selectedPeriod] !== undefined ? (
                      <FetchTable
                        tableArray={groupedByPeriod[selectedPeriod]}
                        referenceTradingCurrency={content.reference_trading_currency}
                        distributor={distributor}
                      />
                    ) : null}
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} md={12} lg={12}>
                  <Typography variant="contentSmall">{t("distributorPortal.revenue.noDataAvailable")}</Typography>
                </Grid>
              )}
            </Grid>
          </Collapse>
        </Box>
      }
    />
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function Widget({
  distributor,
  loadingWidgetRevenueCommission,
  setLoadingWidgetRevenueCommission,
}) {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  Widget.propTypes = {
    distributor: PropTypes.any.isRequired,
    loadingWidgetRevenueCommission: PropTypes.any.isRequired,
    setLoadingWidgetRevenueCommission: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  const { stateStorage, setStateStorage } = useContext(appContext);

  const [error, setError] = useState(false);
  const [, setWarning] = useState(false);
  const [, setSuccess] = useState(false);

  const [loading, setLoading] = useState(navigator.onLine);
  const [content, setContent] = useState();
  const [, setReturnMessage] = useState("");

  const [regularRefreshIntervalCounter, setRegularRefreshIntervalCounter] =
    useState(0);

  // ######################################################
  // Main backend request
  // ######################################################
  function MainRequest(periodic) {
    if (
      localStorage.getItem("myReauthentication") === "false" &&
      navigator.onLine === true
    ) {
      if (distributor !== 0) {
        if (periodic === false) {
          setStateStorage((previousState) => ({
            ...previousState,
            widgetUpdateDistributorPortalRevenueCommission: false,
          }));

          setLoading(true);
          setError(false);
          setWarning(false);
          setSuccess(false);
          setReturnMessage("");
        }

        const sendData = {
          request_type: "dashboard",
          route_info: "distributor_portal",
          widget: "revenue_commission",
          operation: "fetch",
          client: {
            dashboard_access_token:
              stateStorage.userInformation.answer.dashboard_access_token,
          },
          admin: {
            selected_distributor_id: distributor,
          },
          selection: {
            page_start: "",
            page_count: "",
            page_size: "",
            page_order_type: "",
            page_order_by: "",
          },
          filters: {},
        };
        backendRequest(process.env.REACT_APP_URL_API_DASHBOARD, sendData)
          .then((contentFromBackend) => {
            const keyOnly = Object.keys(contentFromBackend);
            const checkKey = keyOnly.includes("message");

            if (checkKey === true) {
              if (contentFromBackend.message === "success") {
                setError(false);
                setLoading(false);
                setLoadingWidgetRevenueCommission(false);
                setContent(contentFromBackend);
              } else if (contentFromBackend.message === "token invalid") {
                setWarning(true);
                setLoading(false);
                setLoadingWidgetRevenueCommission(false);
                setReturnMessage(<TranslatedMessage tKey="distributorPortal.messages.loggedOut" />);

                setStateStorage(stateStorageInit);
                localStorage.setItem("forcedLoggedOut", true);

                localStorage.setItem("myLocalStateStorage", JSON.stringify(""));
                localStorage.removeItem("myLocalStateStorage");

                sessionStorage.setItem("myLocalLastRoute", JSON.stringify(""));
                sessionStorage.removeItem("myLocalLastRoute");

                localStorage.setItem(
                  "myLocalSettingStorage",
                  JSON.stringify("")
                );
                localStorage.removeItem("myLocalSettingStorage");

                localStorage.setItem("myReauthentication", JSON.stringify(""));
                localStorage.removeItem("myReauthentication");
              } else {
                setError(true);
                setLoading(false);
                setLoadingWidgetRevenueCommission(false);
                setReturnMessage(<TranslatedMessage tKey="distributorPortal.messages.invalidRequest" />);
              }
            } else {
              setError(true);
              setLoading(false);
              setLoadingWidgetRevenueCommission(false);
              setReturnMessage(<TranslatedMessage tKey="distributorPortal.common.unknownError" />);
            }
          })
          .catch(() => {
            if (navigator.onLine === true) {
              setError(true);
              setLoading(false);
              setLoadingWidgetRevenueCommission(false);
              setReturnMessage(<TranslatedMessage tKey="distributorPortal.common.unknownError" />);
            }
          });
      }
    }
  }

  // ######################################################
  // Refresh if distributor changes
  // ######################################################
  useEffect(() => {
    MainRequest(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributor]);

  // ######################################################
  // Inital and triggered refreshs
  // ######################################################
  useEffect(() => {
    if (!stateStorage.widgetUpdateDistributorPortalRevenueCommission) return;

    MainRequest(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateStorage.widgetUpdateDistributorPortalRevenueCommission]);

  // ######################################################
  // Regular refreshs
  // ######################################################
  useEffect(() => {
    const timerID = setTimeout(() => {
      setRegularRefreshIntervalCounter(regularRefreshIntervalCounter + 1);
    }, stateStorage.widgetUpdateIntervalDistributorPortalRevenueCommission);

    return () => {
      clearTimeout(timerID);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regularRefreshIntervalCounter]);

  useEffect(() => {
    MainRequest(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regularRefreshIntervalCounter]);

  // ######################################################
  // Data representation
  // ######################################################
  let WidgetOutlet = (
    <Box sx={{ mt: 3 }}>
      <MissingContent message={t("distributorPortal.revenue.shortTitle")} />
    </Box>
  );
  if (loading || loadingWidgetRevenueCommission) {
    WidgetOutlet = (
      <Box sx={{ mt: 3 }}>
        <LoadingAnimation />
      </Box>
    );
  } else if (error) {
    WidgetOutlet = (
      <Box sx={{ mt: 3 }}>
        <MissingContent message={t("distributorPortal.revenue.shortTitle")} />
      </Box>
    );
  } else if (content) {
    WidgetOutlet = (
      <Box sx={{ mt: 3 }}>
        <WidgetMainContentProcessed
          content={content}
          distributor={distributor}
        />
      </Box>
    );
  }

  // ######################################################
  // Outlet
  // ######################################################
  return WidgetOutlet;
}
